<template>
  <Teleport to="body">
    <DialogModal
      :show="show"
      :title="$t('referral.gift.title')"
      :close-label="$t('common.close')"
      @close="close"
    >
      <form class="gift-form" @submit.prevent="submit">
        <input
          ref="codeInput"
          v-model="code"
          class="gift-input"
          :placeholder="$t('referral.gift.placeholder')"
          maxlength="9"
          autocomplete="off"
          :aria-label="$t('referral.gift.title')"
        >
        <p v-if="error" class="gift-error">{{ error }}</p>
        <p v-else class="gift-hint">{{ $t('referral.gift.hint') }}</p>
      </form>
      <template #buttons>
        <button type="button" class="gift-btn secondary" @click="close">{{ $t('common.cancel') }}</button>
        <button type="button" class="gift-btn primary" :disabled="isRedeeming || !code.trim()" @click="submit">
          {{ $t('referral.gift.button') }}
        </button>
      </template>
    </DialogModal>
  </Teleport>
</template>

<script>
import { ElMessage } from 'element-plus'
import DialogModal from '@/components/modals/DialogModal.vue'
import { redeemGiftCode } from '@/composables/useGiftReward'

export default {
  name: 'GiftCodeDialog',
  components: { DialogModal },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      code: '',
      error: '',
      isRedeeming: false
    }
  },
  watch: {
    show(value) {
      if (!value) return
      this.code = ''
      this.error = ''
      this.$nextTick(() => this.$refs.codeInput.focus())
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async submit() {
      if (!this.code.trim()) return
      this.isRedeeming = true
      this.error = ''
      try {
        const result = await redeemGiftCode(this.code)
        const credits = Number(result.credits).toLocaleString(this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US')
        ElMessage.success(this.$t('referral.gift.success', { credits }))
        this.close()
      } catch (error) {
        const key = `referral.gift.errors.${error.code}`
        this.error = this.$te(key) ? this.$t(key) : error.message
      } finally {
        this.isRedeeming = false
      }
    }
  }
}
</script>

<style scoped>
.gift-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.gift-input {
  height: 44px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-lg);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.gift-input::placeholder {
  font-family: var(--font-family-base);
  font-size: var(--font-size-sm);
  letter-spacing: normal;
  text-transform: none;
}

.gift-input:focus-visible {
  outline: none;
  border-color: var(--workspace-accent-dark);
  box-shadow: var(--focus-ring-brand);
}

.gift-hint,
.gift-error {
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.gift-hint {
  color: var(--ink-tertiary);
}

.gift-error {
  color: var(--color-danger);
}

.gift-btn {
  height: 40px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.gift-btn.primary {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.gift-btn.secondary {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.gift-btn:disabled {
  border-color: var(--border-subtle);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.gift-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}
</style>
